/// <amd-module name="Repos/SparseTree/Utils" />
import { SparseTreeFolder, SparseTreeItem, SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import { IObservableLikeValue, ObservableLike } from "VSS/Core/Observable";
import { ITreeItem, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";

/**
 * Assumes the array has already been partitioned in the form of [folders, ..., items]
 * And that each partition is alphabetized by relative path
 * Use this whenever modifying those arrays and you should be fine
 */
export function addToPartitionedSortedArray<T>(
    array: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>[],
    newItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>
) {
    array.splice(getInsertIndexOfPartitionedSortedArray(array, newItem), 0, newItem);
}

/**
 * Assumes the array has already been partitioned in the form of [folders, ..., items]
 * And that each partition is alphabetized by relative path
 * Use this whenever modifying those arrays and you should be fine
 */
export function getInsertIndexOfPartitionedSortedArray<T>(
    array: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>[],
    newItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>
): number {
    const newItemData = ObservableLike.getValue(newItem.data);

    if (newItemData) {
        // Find the first of this kind
        const partitionStartIndex = array.findIndex(x => {
            const testItemData = ObservableLike.getValue(x.data);
            return testItemData ? testItemData.kind === newItemData.kind : false;
        });

        // Find the last of this kind
        let partitionEndIndex = array.findIndex((x, index) => {
            const testItemData = ObservableLike.getValue(x.data);
            return testItemData ? index > partitionStartIndex && testItemData.kind !== newItemData.kind : false;
        });
        if (partitionEndIndex === -1) {
            partitionEndIndex = array.length;
        }

        // If we didn't find a partition start point, put folders at the start and items at the end
        if (partitionStartIndex === -1) {
            if (newItemData.kind === "folder") {
                return 0;
            } else {
                return array.length;
            }
        } else {
            const partition = array.slice(partitionStartIndex, partitionEndIndex);
            const relativeInsertIndex = partition.findIndex(x => {
                const testItemData = ObservableLike.getValue(x.data);
                return testItemData ? testItemData.relativeIdentifier.localeCompare(newItemData.relativeIdentifier, navigator.language) > 0 : false;
            });

            if (relativeInsertIndex !== -1) {
                return partitionStartIndex + relativeInsertIndex;
            } else {
                return partitionEndIndex;
            }
        }
    } else {
        return array.length;
    }
}

export function getInsertIndexOfPartitionSortedTreeItems<T>(
    array: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[],
    newItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>
): number {
    if (array.length === 0) {
        return 0;
    }

    const initialDepth = array[0].depth;
    const itemsAtInitialDepth = array.filter(x => x.depth === initialDepth);
    const newItemData = ObservableLike.getValue(newItem.data);

    if (newItemData) {
        if (newItemData.kind === "folder") {
            const insertBeforeIndexAtInitialDepth = getInsertIndexOfPartitionedSortedArray(itemsAtInitialDepth.map(x => x.underlyingItem), newItem);

            if (insertBeforeIndexAtInitialDepth >= itemsAtInitialDepth.length) {
                return array.length;
            } else {
                const insertBeforeItem = itemsAtInitialDepth[insertBeforeIndexAtInitialDepth];
                const insertBeforeIndex = array.findIndex(testItem => testItem === insertBeforeItem);
                return insertBeforeIndex;
            }
        } else {
            const sizeDueToGrandchildren = array.length - itemsAtInitialDepth.length;
            return getInsertIndexOfPartitionedSortedArray(itemsAtInitialDepth.map(x => x.underlyingItem), newItem) + sizeDueToGrandchildren;
        }
    } else {
        return array.length;
    }
}
